@if (loaded(); as isLoaded) {
  <hyperkit-dialog class="hyperkit-component" [headerText]="options.headerText">
    @if (step() === 'select') {
      <hyperkit-typography
        class="dialog-description"
        display="block"
        variant="body"
        size="sm"
        align="left"
        color="body"
      >
        Select one of the pivot approaches below.
      </hyperkit-typography>
      <hyperkit-card (click)="chooseRecommendedPivots()">
        <div>
          <hyperkit-typography variant="emphasized" size="sm" color="primary">
            Use Suggested Pivots (recommended)
          </hyperkit-typography>
          <hyperkit-typography variant="body" size="sm" color="secondary">
            Generate recommended pivots based on your query results
          </hyperkit-typography>
        </div>
        <hyperkit-icon
          icon="arrow-right"
          size="base"
          color="primary"
          class="code-icon"
        ></hyperkit-icon>
      </hyperkit-card>
      <hyperkit-card (click)="chooseYourOwnPivots()">
        <div>
          <hyperkit-typography variant="emphasized" size="sm" color="primary">
            Choose Your Pivots
          </hyperkit-typography>
          <hyperkit-typography variant="body" size="sm" color="secondary">
            Select from the full list of available pivots
          </hyperkit-typography>
        </div>
        <hyperkit-icon
          icon="arrow-right"
          size="base"
          color="primary"
          class="code-icon"
        ></hyperkit-icon>
      </hyperkit-card>
    } @else {
      <hyperkit-breadcrumb-list
        class="breadcrumb-list"
        [items]="breadcrumbs()"
        (breadcrumbClick)="onBreadcrumbClick($event)"
      ></hyperkit-breadcrumb-list>
    }

    @if (step() === 'capture') {
      @if (chooseYourOwn()) {
        <hyperkit-typography
          class="dialog-description"
          display="block"
          variant="body"
          size="sm"
          align="left"
          color="body"
        >
          Select one or more pivots from the library below to guide your
          investigation.
        </hyperkit-typography>
      } @else {
        <hyperkit-typography
          class="dialog-description"
          display="block"
          variant="body"
          size="sm"
          align="left"
          color="body"
        >
          Please complete the fields below using details from your previous
          query results. Recommended pivots will be generated based on your
          input.
        </hyperkit-typography>
      }
      <form [formGroup]="dataCaptureForm()">
        <div class="scrollable-body">
          <hyperkit-accordion title="Required Fields" [expanded]="true">
            <div class="form-grid">
              @for (field of primaryFields(); track field) {
                <div>
                  <hyperkit-input
                    [formControlName]="field"
                    [id]="field"
                    [label]="toLabel(field)"
                    class="hyperkit-component"
                    type="text"
                  ></hyperkit-input>
                </div>
              }
            </div>
          </hyperkit-accordion>
          @if (secondaryFields().length > 0) {
            <hyperkit-accordion title="Optional Fields" [expanded]="false">
              <div class="form-grid">
                @for (field of secondaryFields(); track field) {
                  <div>
                    <hyperkit-input
                      [formControlName]="field"
                      [id]="field"
                      [label]="toLabel(field)"
                      class="hyperkit-component"
                      type="text"
                    ></hyperkit-input>
                  </div>
                }
              </div>
            </hyperkit-accordion>
          }
        </div>
        <div class="dialog-footer">
          <hyperkit-button
            class="clear-btn"
            color="secondary"
            size="small"
            text="Clear"
            (click)="clearForm()"
          ></hyperkit-button>
          @if (chooseYourOwn()) {
            <hyperkit-button
              color="secondary"
              size="small"
              text="Back"
              (click)="backToPivotList()"
            ></hyperkit-button>
            <hyperkit-button
              class="hyperkit-component"
              size="small"
              color="primary"
              text="Continue"
              (click)="onSubmit()"
            ></hyperkit-button>
          } @else {
            <hyperkit-button
              color="secondary"
              size="small"
              text="Back"
              (click)="backToSelect()"
            ></hyperkit-button>
            <hyperkit-button
              class="hyperkit-component"
              size="small"
              color="primary"
              text="Continue"
              (click)="onContinue()"
            ></hyperkit-button>
          }
        </div>
      </form>

      @if (error()) {
        <hyperkit-error [error]="error()"></hyperkit-error>
      }
    }

    @if (step() === 'pivot') {
      @if (chooseYourOwn()) {
        <hyperkit-typography
          class="dialog-description"
          variant="body"
          size="sm"
          align="left"
          color="body"
        >
          Select one or more pivots to guide your investigation.
        </hyperkit-typography>
      } @else {
        <hyperkit-typography
          class="dialog-description"
          variant="body"
          size="sm"
          align="left"
          color="body"
        >
          Based on the data you’ve provided, we suggest the following pivot
          queries to guide your investigation. Select one or more that best fit
          your needs.
        </hyperkit-typography>
      }

      <div class="pivot-search-container">
        <hyperkit-search-input
          [placeholder]="'Search pivots...'"
          [text]="pivotSearchTerm()"
          (textChange)="pivotSearchTerm.set($event)"
          class="search-bar"
        ></hyperkit-search-input>
      </div>

      <div class="pivot-scroll-container">
        <div class="pivot-cards-container">
          @if (filteredItems().length > 0) {
            @for (item of filteredItems(); track item.UUID) {
              <pivot-card
                [title]="item.title"
                [description]="item.description"
                [selected]="selectedPivotItems().has(item.UUID)"
                [required]="item.pivot_fields.required"
                [optional]="item.pivot_fields.optional"
                source="recommended"
                (pivotSelection)="togglePivotSelection(item)"
              ></pivot-card>
            }
          } @else {
            <hyperkit-typography
              class="dialog-description"
              display="block"
              variant="body"
              size="sm"
              align="center"
              color="body"
            >
              No compatible pivots found. Add more capture data and try again.
            </hyperkit-typography>
          }
        </div>
      </div>
      <hyperkit-typography
        class="dialog-description"
        display="block"
        variant="emphasized"
        size="sm"
        align="center"
        color="body"
      >
        Selected Pivots: {{ selectedPivotItems().size }}
      </hyperkit-typography>
      <div class="dialog-footer">
        @if (chooseYourOwn()) {
          <hyperkit-button
            color="secondary"
            text="Back"
            size="small"
            (click)="backToSelect()"
          ></hyperkit-button>
          <hyperkit-button
            [disabled]="selectedPivotItems().size === 0"
            class="send-button hyperkit-component"
            color="primary"
            size="small"
            icon="arrow-right"
            iconPosition="right"
            text="Continue to Fields"
            (click)="generateInputs()"
          ></hyperkit-button>
        } @else {
          <hyperkit-button
            color="secondary"
            text="Back"
            size="small"
            (click)="goBack()"
          ></hyperkit-button>
          <hyperkit-button
            class="send-button hyperkit-component"
            color="primary"
            size="small"
            text="Add Pivots"
            (click)="onSubmit()"
          ></hyperkit-button>
        }
      </div>
    }
  </hyperkit-dialog>
} @else {
  <p>Loading…</p>
}
